<template>
    <div class="bloco margem">
        <h2>Registro de Alterações</h2>

        <div v-if="erro" class="mensagem-erro">{{ erro }}</div>
        <div v-else-if="loading" class="loading">
            <div></div>
        </div>
        <template v-else>
            <div class="filtro-origem margem-inferior" v-if="protocolo.tarefas && protocolo.tarefas.length > 0">
                <label for="filtro-tarefa">Filtrar por:</label>
                <select id="filtro-tarefa" v-model="filtroTarefaId" @change="carregarAlteracoes">
                    <option :value="null">Todas as alterações</option>
                    <option value="cabecalho">Cabeçalho do protocolo</option>
                    <option v-for="t in protocolo.tarefas" :key="t.id" :value="t.id">
                        {{ t.codigo }} – {{ truncar(t.descricao, 50) }}
                    </option>
                </select>
            </div>

            <div v-if="alteracoes.length === 0" class="sem-registros">
                Nenhuma alteração registrada.
            </div>
            <div v-else class="tabela-wrapper">
                <table class="tabela">
                    <thead>
                        <tr>
                            <th>Data / Hora</th>
                            <th>Usuário</th>
                            <th>Origem</th>
                            <th>Campo</th>
                            <th>Valor anterior</th>
                            <th>Valor novo</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="a in alteracoes" :key="a.id">
                            <td>{{ formatarDataHora(a.created_at) }}</td>
                            <td>{{ a.usuario_nome || '—' }}</td>
                            <td>{{ a.origem === 'cabecalho' ? 'Cabeçalho' : a.tarefa_descricao }}</td>
                            <td>{{ labelColuna(a.coluna) }}</td>
                            <td class="valor-cell">{{ exibirValorColuna(a) }}</td>
                            <td class="valor-cell">{{ exibirValorColunaNovo(a) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </template>
    </div>
</template>

<script>
import serviceProtocolo from "@/services/serviceProtocolo";

const LABELS_COLUNAS = {
    nome: 'Nome',
    responsavel_id: 'Responsável',
    dtFim: 'Data fim',
    setor_id: 'Setor',
    setorDestinatario_id: 'Setor destinatário',
    dtFimPrevisto: 'Data fim prevista',
    obs: 'Observação',
    status: 'Status',
    dtInicio: 'Data/hora início',
    participantes: 'Participantes',
    responsaveis: 'Outros responsáveis',
    descricao: 'Descrição',
    HP: 'HP',
    dtInicioReal: 'Data início real',
    dtFimReal: 'Data fim real',
    parente_id: 'Tarefa pai',
    isRead: 'Lido',
    protocoloAssociado_id: 'Protocolo associado',
};

export default {
    name: 'AlteracoesProtocolo',
    props: {
        protocolo: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            alteracoes: [],
            loading: false,
            erro: null,
            filtroTarefaId: null,
        };
    },
    watch: {
        'protocolo.id': {
            handler(id) {
                if (id) {
                    this.filtroTarefaId = null;
                    this.carregarAlteracoes();
                }
            },
        },
    },
    mounted() {
        if (this.protocolo?.id) {
            this.carregarAlteracoes();
        }
    },
    methods: {
        async carregarAlteracoes() {
            if (!this.protocolo?.id) return;
            this.loading = true;
            this.erro = null;
            try {
                const tarefaId = this.filtroTarefaId === 'cabecalho' ? null : this.filtroTarefaId;
                const params = { protocolo_id: this.protocolo.id };
                if (tarefaId !== null && tarefaId !== 'cabecalho') {
                    params.tarefa_id = tarefaId;
                } else if (this.filtroTarefaId === 'cabecalho') {
                    const todas = await serviceProtocolo.listarAlteracoes(this.protocolo.id);
                    this.alteracoes = todas.filter(a => a.origem === 'cabecalho');
                    this.loading = false;
                    return;
                }
                this.alteracoes = await serviceProtocolo.listarAlteracoes(
                    this.protocolo.id,
                    tarefaId ?? undefined
                );
            } catch (e) {
                this.erro = e?.response?.data?.error || 'Erro ao carregar alterações.';
                this.alteracoes = [];
            } finally {
                this.loading = false;
            }
        },
        formatarDataHora(iso) {
            if (!iso) return '—';
            try {
                const d = new Date(iso);
                return d.toLocaleString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                });
            } catch {
                return iso;
            }
        },
        labelColuna(coluna) {
            return LABELS_COLUNAS[coluna] ?? coluna;
        },
        exibirValor(v) {
            if (v === null || v === undefined || v === '') return '—';
            return String(v);
        },
        /**
         * Valor anterior: lista completa como estava (participantes/responsaveis = mesmo que demais colunas).
         */
        exibirValorColuna(a) {
            return this.exibirValor(a.valor_antigo_label ?? a.valor_antigo);
        },
        /**
         * Valor novo: para participantes e responsaveis mostra o delta com + (adicionado) e - (removido).
         * Demais colunas: valor_novo_label ou valor_novo.
         */
        exibirValorColunaNovo(a) {
            if (a.coluna !== 'participantes' && a.coluna !== 'responsaveis') {
                return this.exibirValor(a.valor_novo_label ?? a.valor_novo);
            }
            const antigoIds = (a.valor_antigo || '').split(',').map((s) => s.trim()).filter(Boolean);
            const novoIds = (a.valor_novo || '').split(',').map((s) => s.trim()).filter(Boolean);
            const adicionados = novoIds.filter((id) => !antigoIds.includes(id));
            const removidos = antigoIds.filter((id) => !novoIds.includes(id));
            const antigoNomes = (a.valor_antigo_label || '').split(',').map((s) => s.trim());
            const antigoIdsOrder = (a.valor_antigo || '').split(',').map((s) => s.trim()).filter(Boolean);
            const novoNomes = (a.valor_novo_label || '').split(',').map((s) => s.trim());
            const novoIdsOrder = (a.valor_novo || '').split(',').map((s) => s.trim()).filter(Boolean);
            const nomeAntigoPorId = {};
            antigoIdsOrder.forEach((id, i) => {
                nomeAntigoPorId[id] = antigoNomes[i] ?? id;
            });
            const nomeNovoPorId = {};
            novoIdsOrder.forEach((id, i) => {
                nomeNovoPorId[id] = novoNomes[i] ?? id;
            });
            const partes = [];
            adicionados.forEach((id) => partes.push('+ ' + (nomeNovoPorId[id] ?? id)));
            removidos.forEach((id) => partes.push('- ' + (nomeAntigoPorId[id] ?? id)));
            if (partes.length === 0) return '—';
            return partes.join(', ');
        },
        truncar(texto, max) {
            if (!texto) return '';
            const s = String(texto);
            return s.length <= max ? s : s.slice(0, max) + '…';
        },
    },
};
</script>

<style scoped>
.filtro-origem {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.filtro-origem label {
    font-weight: 500;
}

.filtro-origem select {
    min-width: 280px;
    padding: 0.35rem 0.5rem;
}

.margem-inferior {
    margin-bottom: 1rem;
}

.sem-registros {
    color: #666;
    font-style: italic;
    padding: 1rem 0;
}

.tabela-wrapper {
    overflow-x: auto;
}

.valor-cell {
    max-width: 200px;
    word-break: break-word;
}
</style>
